import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/** Cache of product URL -> gallery HTML for sibling swatch previews */
const siblingGalleryCache = new Map();

/** Tile attributes that name the product a Constructor tile currently shows (PN-2236). */
const BEACON_TILE_ATTRS = [
  'data-cnstrc-item-id',
  'data-cnstrc-item-name',
  'data-cnstrc-item-variation-id',
  'data-cnstrc-item-price',
  'data-product-handle',
  'data-product-id',
];

/**
 * Timestamp of the last real pointer movement. A swatch `pointerenter` that fires
 * without a recent `pointermove` was caused by the page scrolling under a
 * stationary cursor (a "fake" hover) rather than the shopper moving onto the
 * swatch — we skip loading a sibling gallery in that case. Genuine move-hovers
 * fire `pointermove` within ~a frame of `pointerenter`, so this stays instant and
 * needs no dwell delay.
 */
let lastPointerMoveAt = 0;
let pointerMoveTrackerInstalled = false;

/** ~one animation frame of grace between a genuine move and the swatch enter. */
const HOVER_MOVE_WINDOW_MS = 100;

function installPointerMoveTracker() {
  if (pointerMoveTrackerInstalled || typeof window === 'undefined') return;
  pointerMoveTrackerInstalled = true;
  window.addEventListener(
    'pointermove',
    () => {
      lastPointerMoveAt = performance.now();
    },
    { passive: true },
  );
}

/** True when the cursor actually moved recently — i.e. this is a real hover, not a scroll. */
function isGenuineHover() {
  return performance.now() - lastPointerMoveAt <= HOVER_MOVE_WINDOW_MS;
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Updates all of the card's navigation links (title, gallery, stretched card link)
   * to a new product URL. Used when a sibling swatch (a separate product) is
   * selected so every clickable region of the card lands on that sibling's PDP.
   * @param {string} url - The product URL to navigate to.
   */
  updateProductLinkUrl(url) {
    const { productCardLink, cardGalleryLink, productTitleLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) productCardLink.href = url;
    if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = url;
    if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = url;
  }

  /** Beacon attributes the tile was rendered with, restored when the main swatch is re-picked. */
  /** @type {Record<string, string | null> | null} */
  #beaconOrigin = null;

  /**
   * Re-stamps the Constructor tile's item attributes for the selected swatch
   * (PN-2236). Only tiles that already carry `data-cnstrc-item-id` are touched,
   * so native Liquid cards and consent-swept tiles stay as they are. A sibling's
   * identity comes from the card-gallery section's `[data-tyz-beacon-item]`.
   * @param {string} productUrl - The selected swatch's product URL
   * @param {boolean} isMain - Whether the selection is the tile's own product
   */
  async syncBeaconItem(productUrl, isMain) {
    const tile = this.closest('[data-cnstrc-item-id]');
    if (!(tile instanceof HTMLElement)) return;

    this.#beaconOrigin ??= Object.fromEntries(BEACON_TILE_ATTRS.map((name) => [name, tile.getAttribute(name)]));

    /** @type {Record<string, string | null>} */
    let next = this.#beaconOrigin;
    if (!isMain) {
      const html = await ProductCard.#fetchGalleryHtml(productUrl).catch(() => null);
      const carrier = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tyz-beacon-item]');
      if (!(carrier instanceof HTMLElement)) return;
      const price = Number(carrier.dataset.price);
      next = {
        'data-cnstrc-item-id': carrier.dataset.itemId || null,
        'data-cnstrc-item-name': carrier.dataset.itemName || null,
        'data-cnstrc-item-variation-id': carrier.dataset.variationId || null,
        'data-cnstrc-item-price': carrier.dataset.price && Number.isFinite(price) ? (price / 100).toFixed(2) : null,
        'data-product-handle': carrier.dataset.handle || null,
        'data-product-id': carrier.dataset.itemId || null,
      };
    }

    // A later click may have landed while the gallery was fetching; only the
    // swatch still selected gets to write. Consent sweeps remove the item id,
    // and nothing is re-stamped once it has gone.
    const checked = this.querySelector('sibling-swatches-component input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.productUrl !== productUrl) return;
    if (!tile.hasAttribute('data-cnstrc-item-id')) return;

    for (const name of BEACON_TILE_ATTRS) {
      const value = next[name];
      if (value) tile.setAttribute(name, value);
      else tile.removeAttribute(name);
    }
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    installPointerMoveTracker();
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component')
      || this.querySelector('sibling-swatches-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant (for variant swatches - selects existing slide by media id).
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /** @type {AbortController | null} */
  #galleryFetchController = null;

  /**
   * Fetches gallery HTML for a product URL, using cache when available.
   * @param {string} productUrl - Full product URL (e.g. /products/handle)
   * @param {AbortSignal} [signal] - Optional abort signal for the fetch
   * @returns {Promise<string | null>} Gallery HTML or null
   */
  static async #fetchGalleryHtml(productUrl, signal) {
    const cacheKey = new URL(productUrl, window.location.origin).pathname;
    const cached = siblingGalleryCache.get(cacheKey);
    if (cached) return cached;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', 'section-rendering-card-gallery');

    const res = await fetch(url.toString(), { signal });
    const html = await res.text();
    siblingGalleryCache.set(cacheKey, html);
    return html;
  }

  /**
   * Fetches or retrieves gallery for a product URL and morphs it into the card.
   * Uses cache when available for instant display.
   * @param {string} productUrl - Product URL
   */
  #loadAndMorphGallery(productUrl) {
    if (!this.refs.cardGallery) return;

    const cacheKey = new URL(productUrl, window.location.origin).pathname;
    const cached = siblingGalleryCache.get(cacheKey);
    if (cached) {
      const doc = new DOMParser().parseFromString(cached, 'text/html');
      const newGallery = doc.querySelector('[ref="cardGallery"], .card-gallery');
      if (newGallery) {
        this.#morphGalleryContents(newGallery);
      }
      return;
    }

    this.#abortGalleryFetch();
    this.#galleryFetchController = new AbortController();

    ProductCard.#fetchGalleryHtml(productUrl, this.#galleryFetchController.signal)
      .then((html) => {
        if (!html) return;
        this.#galleryFetchController = null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGallery = doc.querySelector('[ref="cardGallery"], .card-gallery');
        if (newGallery && this.refs.cardGallery) {
          this.#morphGalleryContents(newGallery);
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err);
        this.#galleryFetchController = null;
      });
  }

  /**
   * Morphs only the gallery link/slideshow into the card, preserving wishlist, quick-add, and other children.
   * Also restores the main product URL on the link.
   * @param {Element} newGallery
   */
  #morphGalleryContents(newGallery) {
    const currentLink = this.refs.cardGallery?.querySelector('a[ref="cardGalleryLink"], a.contents');
    const newLink = newGallery?.querySelector('a[ref="cardGalleryLink"], a.contents');
    if (!(currentLink instanceof HTMLAnchorElement) || !newLink) return;

    morph(currentLink, newLink);
    this.#initLqipImages();

    // Restore the gallery link to whichever swatch is currently selected (checked),
    // not always the main product - a sibling may have just been clicked (permanent
    // selection), not merely hovered (temporary preview).
    const siblingSwatches = this.querySelector('sibling-swatches-component');
    const checkedInput = siblingSwatches?.querySelector('input[type="radio"]:checked');
    const selectedProductUrl =
      checkedInput instanceof HTMLInputElement
        ? checkedInput.dataset.productUrl
        : siblingSwatches instanceof HTMLElement
          ? siblingSwatches.dataset.productUrl
          : undefined;
    if (selectedProductUrl) {
      currentLink.href = selectedProductUrl;
    }
  }

  /**
   * Ensures LQIP images that are already loaded get the 'loaded' class so the high-res
   * image is shown. Fixes cases where onload didn't fire (e.g. cached images after morph).
   */
  #initLqipImages() {
    requestAnimationFrame(() => {
      this.querySelectorAll('.product-media--lqip:not(.loaded)').forEach((media) => {
        const img = media.querySelector('.product-media__image--lqip');
        if (!(img instanceof HTMLImageElement)) return;

        if (img.complete) {
          media.classList.add('loaded');
        } else {
          img.addEventListener('load', () => media.classList.add('loaded'), { once: true });
          img.addEventListener('error', () => media.classList.add('loaded'), { once: true });
        }
      });
    });
  }

  /**
   * Loads a product's gallery (main or sibling) by fetching via Section Rendering API and morphing into the card.
   * Uses cache when available. Fetches ONLY the hovered sibling (cached thereafter),
   * so galleries for swatches the shopper never hovers are never requested.
   * @param {PointerEvent | { target: Element }} event - Event with target being the swatch label.
   */
  loadSiblingGallery(event) {
    // Ignore scroll-induced enters (a swatch moving under a stationary cursor);
    // only load when the shopper actually moved onto the swatch.
    if (!isGenuineHover()) return;

    const target = event.target instanceof Element ? event.target : null;
    const swatch = target?.closest?.('.variant-option__swatch');
    const input = swatch?.querySelector('input[data-product-url]');
    const productUrl = input instanceof HTMLInputElement ? input.dataset.productUrl : null;

    if (!productUrl || !this.refs.cardGallery) return;

    this.restoreMainGallery.cancel();
    this.#loadAndMorphGallery(productUrl);
  }

  /**
   * Restores the gallery to the currently selected (checked) swatch.
   * Skips when pointer is still over a swatch to avoid flicker when moving between swatches.
   */
  #performRestoreMainGallery = () => {
    const siblingSwatches = this.querySelector('sibling-swatches-component');
    const mainProductUrl = siblingSwatches instanceof HTMLElement ? siblingSwatches.dataset.productUrl : undefined;
    if (!mainProductUrl || !this.refs.cardGallery) return;

    const checkedInput = siblingSwatches?.querySelector('input[type="radio"]:checked');
    if (!(checkedInput instanceof HTMLInputElement)) return;

    const checkedProductUrl = checkedInput.dataset.productUrl;
    if (!checkedProductUrl) return;

    this.#loadAndMorphGallery(checkedProductUrl);
  };

  /**
   * Restores the main product's gallery when leaving a sibling swatch (hover).
   * Does not run when a sibling is selected (clicked) - keeps that gallery visible.
   * Debounced to avoid briefly restoring when moving between swatches.
   */
  restoreMainGallery = debounce(this.#performRestoreMainGallery, 100);

  /**
   * Restores the main gallery immediately (e.g. when user clicks the main product swatch).
   */
  restoreMainGalleryImmediate() {
    this.restoreMainGallery.cancel();
    this.#performRestoreMainGallery();
  }

  #abortGalleryFetch() {
    if (this.#galleryFetchController) {
      this.#galleryFetchController.abort();
      this.#galleryFetchController = null;
    }
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    // Constructor's click tracker mirrors this selector — keep both in sync.
    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * A custom element that displays sibling product swatches.
 * Sibling products are separate products linked via akeneo.sibling_products metafield.
 * Image preview is handled declaratively via on:pointerenter events.
 * This component handles swatch selection state and card link URL updates.
 *
 * @typedef {object} SiblingSwatchesRefs
 * @property {OverflowList} overflowList - The overflow list component.
 *
 * @extends {Component<SiblingSwatchesRefs>}
 */
class SiblingSwatchesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
  }

  /**
   * Handles the radio input change event.
   * Updates the parent product card's link URLs to the selected sibling's product URL
   * and loads that sibling's images into the card gallery slideshow (for tap support).
   * @param {Event} event - The change event.
   */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const productUrl = event.target.dataset.productUrl;
    if (!productUrl) return;

    /** @type {ProductCard | null} */
    const productCard = this.closest('product-card');
    if (!productCard) return;

    const mainProductUrl = this.dataset.productUrl;

    productCard.updateProductLinkUrl(productUrl);
    void productCard.syncBeaconItem(productUrl, productUrl === mainProductUrl);

    if (productUrl === mainProductUrl) {
      productCard.restoreMainGalleryImmediate();
    } else if (typeof productCard.loadSiblingGallery === 'function') {
      productCard.loadSiblingGallery({ target: event.target });
    }
  };

  /**
   * Gets the selected option (checked radio input).
   * Compatible with ProductCard.resetVariant() which reads
   * this.variantPicker.selectedOption.dataset.optionMediaId.
   * @returns {HTMLInputElement | undefined}
   */
  get selectedOption() {
    const selected = this.querySelector('input[type="radio"]:checked');
    return selected instanceof HTMLInputElement ? selected : undefined;
  }

  /**
   * Shows all swatches by expanding the overflow list.
   * @param {Event} [event] - The event that triggered the expansion.
   */
  showAllSwatches(event) {
    event?.preventDefault();
    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('sibling-swatches-component')) {
  customElements.define('sibling-swatches-component', SiblingSwatchesComponent);
}
